"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { countWords } from "@/lib/tool-kit/essay-templates/text";
import {
  buildStagedReflection,
  type StagedLabels,
} from "@/lib/tool-kit/research-ops/staged-reflection";

/** The form and document labels both reflection generators share. */
export interface StagedFormLabels {
  tryExample: string;
  clear: string;
  topicLabel: string;
  topicPlaceholder: string;
  headingsLabel: string;
  stagesHeading: string;
  stageFieldLabel: string;
  triggersHint: string;
  wordCount: string;
  fileName: string;
  document: {
    heading: string;
    prompt: string;
    guidance: string;
    copy: string;
    copied: string;
    downloadText: string;
    downloadMarkdown: string;
    print: string;
    remaining: string;
    complete: string;
  };
}

type Example<S extends string> = { topic: string } & Record<S, string>;

/** A record with one entry per stage. */
function fill<S extends string>(
  order: readonly S[],
  value: (stage: S) => string
): Record<S, string> {
  const record: Partial<Record<S, string>> = {};
  for (const stage of order) {
    record[stage] = value(stage);
  }
  return record as Record<S, string>;
}

/**
 * The shared body of a staged reflection generator: a topic, one box per stage with its
 * guiding question and word count, and the structured document with copy, download and
 * print. The tool supplies its stages and strings; the widget file stays a few lines.
 */
export function StagedReflectionForm<S extends string>({
  idPrefix,
  order,
  labels,
  form,
  example,
}: {
  idPrefix: string;
  order: readonly S[];
  labels: StagedLabels<S>;
  form: StagedFormLabels;
  example: Example<S>;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const blank = useMemo(() => fill(order, () => ""), [order]);
  const [topic, setTopic] = useState("");
  const [headings, setHeadings] = useState(true);
  const [stages, setStages] = useState<Record<S, string>>(blank);
  const doc = useMemo(
    () => buildStagedReflection(order, { topic, headings, stages }, labels),
    [order, topic, headings, stages, labels]
  );

  const loadExample = () => {
    setTopic(example.topic);
    setStages(fill(order, (stage) => example[stage]));
  };
  const clear = () => {
    setTopic("");
    setStages(blank);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {form.tryExample}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clear} type="button">
          {form.clear}
        </button>
      </div>

      <Panel>
        <TextField
          id={`${idPrefix}-topic`}
          label={form.topicLabel}
          onChange={setTopic}
          placeholder={form.topicPlaceholder}
          value={topic}
        />
        <Checkbox
          checked={headings}
          label={form.headingsLabel}
          onChange={setHeadings}
        />
      </Panel>

      <Panel title={form.stagesHeading}>
        {order.map((stage, index) => {
          const words = countWords(stages[stage]);
          const triggers = labels.stageTriggers?.[stage] ?? [];
          return (
            <TextAreaField
              hint={
                <>
                  {labels.stageGuides[stage]}
                  {triggers.length > 0
                    ? ` ${t(form.triggersHint, { questions: triggers.join(" ") })}`
                    : null}{" "}
                  <span className="whitespace-nowrap font-medium">
                    · {fmt(words)}
                    {tPlural(form.wordCount, locale, words)}
                  </span>
                </>
              }
              id={`${idPrefix}-${stage}`}
              key={stage}
              label={t(form.stageFieldLabel, {
                n: index + 1,
                stage: labels.stageNames[stage],
                question: labels.stageQuestions[stage],
              })}
              onChange={(value) =>
                setStages((previous) => ({ ...previous, [stage]: value }))
              }
              prose
              rows={4}
              value={stages[stage]}
            />
          );
        })}
      </Panel>

      <DocumentPreview
        doc={doc}
        fileName={form.fileName}
        idPrefix={idPrefix}
        labels={form.document}
      />
    </div>
  );
}
